import { useCallback, useEffect, useState } from "react";
import type { AttachmentDto } from "@grizzly/shared";
import { api, attachmentContentUrl } from "../../lib/apiClient.js";
import type { ApiClientError } from "../../lib/apiClient.js";
import { formatBytes, formatDateTime } from "../../lib/format.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { useToast } from "../../lib/useToast.js";
import { useHasPermission } from "../../lib/session.js";
import { useI18n } from "../../i18n/index.js";
import { Download, Trash2 } from "lucide-react";

export function JobDocumentPanel({ jobId }: { jobId: string }) {
  const { push } = useToast();
  const { t } = useI18n();
  const canWrite = useHasPermission("attachments:write");
  const canDelete = useHasPermission("attachments:delete");
  const [items, setItems] = useState<AttachmentDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.listAttachments("job", jobId);
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadAttachment({ ownerType: "job", ownerId: jobId, file });
      push(t("jobs.documents.uploaded"), file.name);
      setFile(null);
      await load();
    } catch (err) {
      const e = err as ApiClientError;
      push(e.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [file, jobId, load, push, t]);

  const remove = useCallback(
    async (item: AttachmentDto) => {
      try {
        await api.deleteAttachment(item.id, "Removed from job");
        push(t("jobs.documents.removed"));
        await load();
      } catch (err) {
        const e = err as ApiClientError;
        push(e.message, undefined, "danger");
      }
    },
    [load, push, t]
  );

  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold text-ink">{t("jobs.documents.title")}</h2>
      {error ? (
        <p role="alert" className="text-sm text-action-danger">
          {error}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">{t("jobs.documents.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-2 rounded border border-line px-3 py-2"
            >
              <div className="min-w-0 text-sm">
                <p className="truncate font-medium text-ink">{item.originalName}</p>
                <p className="text-xs text-ink-muted">
                  {formatBytes(item.sizeBytes)} · {formatDateTime(item.createdAt)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" asChild>
                  <a href={attachmentContentUrl(item.id)}>
                    <Download className="h-4 w-4" aria-hidden="true" />{" "}
                    {t("jobs.documents.download")}
                  </a>
                </Button>
                {canDelete ? (
                  <Button size="sm" variant="ghost" onClick={() => void remove(item)}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {canWrite ? (
        <div className="mt-3 flex items-end gap-2 border-t border-line pt-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="job-file" className="text-sm font-medium text-ink">
              {t("jobs.documents.attachLabel")}
            </label>
            <input
              id="job-file"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="h-11 rounded-[var(--radius-card)] border border-line bg-surface-raised px-2 py-2 text-sm file:me-2 file:border-0 file:bg-surface-inset file:rounded file:px-3 file:py-1.5"
            />
          </div>
          <Button variant="primary" disabled={busy || !file} onClick={upload}>
            {busy ? t("jobs.documents.uploading") : t("jobs.documents.upload")}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
